import { useMemo } from 'react';

import type { BizyHttpClient, BizyHttpRequestOptions } from '@bonusly/bizy-kit';
import { useHttpClient } from '@bonusly/http-client';

/**
 * Adapts Kaleidoscope's `useHttpClient()` (axios-style, responses wrapped as
 * `{ data: T }`) to the transport-agnostic `BizyHttpClient` contract the shared
 * `@bonusly/bizy-kit` connector expects — methods return `Promise<T>` directly.
 *
 * The package never sees the `{ data }` envelope: every method unwraps `.data`
 * here. `get` forwards `params`/`headers`; `post`/`patch` take a JSON body only
 * (the connector has no need for per-call request config).
 */
export function useBizyHttpClient(): BizyHttpClient {
	const http = useHttpClient();

	return useMemo<BizyHttpClient>(
		() => ({
			get: <T>(path: string, options?: BizyHttpRequestOptions) =>
				http.get<T>(path, { params: options?.params, headers: options?.headers }).then((res) => res.data),
			post: <T>(path: string, body?: unknown) => http.post<T>(path, body).then((res) => res.data),
			patch: <T>(path: string, body?: unknown) => http.patch<T>(path, body).then((res) => res.data),
			delete: <T>(path: string) => http.delete<T>(path).then((res) => res.data),
		}),
		[http]
	);
}
